import { lowerCase, upperFirst } from "lodash-es"

import type { CompanyRunRow } from "@/admin/monitoring/monitoring-console-data"
import { AssessmentOutcome } from "@/constants/terminal/assessment-outcome"
import {
  CompanyRunOutcome,
  dataByCompanyRunOutcome,
} from "@/constants/terminal/company-run-outcome"
import { ResearchSufficiencyOutcome } from "@/constants/terminal/research-sufficiency-outcome"
import { SearchGeographyKind } from "@/constants/terminal/search-geography-kind"
import type { SearchGeography } from "@/terminal/research/value-schemas"

// Stored keys (source keys, record kinds, JSON fields) are open-ended
// identifiers with no label registry. Some carry a provider identifier after a
// colon (place:ChIJ…) that must survive verbatim.
export const humanizeKey = (key: string) => {
  const [kind = "", ...identifier] = key.split(":")
  return [upperFirst(lowerCase(kind)), ...identifier].join(" ")
}

// The counties Terminal Policy searches, by FIPS code. A FIPS code nobody can
// read is no use on screen; a county missing here falls back to its code.
const countyByFipsCode: Record<string, { name: string; state: string }> = {
  "04013": { name: "Maricopa", state: "AZ" },
  "06073": { name: "San Diego", state: "CA" },
  "12086": { name: "Miami-Dade", state: "FL" },
  "32003": { name: "Clark", state: "NV" },
  "37119": { name: "Mecklenburg", state: "NC" },
  "48201": { name: "Harris", state: "TX" },
  "49049": { name: "Utah", state: "UT" },
}

const formatCounty = (fipsCode: string) => {
  const county = countyByFipsCode[fipsCode]
  return county ? `${county.name} County, ${county.state}` : `FIPS ${fipsCode}`
}

// A Research Sufficiency gap can also name a formation state no one knows.
export const formatSearchGeography = (
  geography:
    | SearchGeography
    | { kind: SearchGeographyKind.FORMATION_JURISDICTION_UNKNOWN }
    | null
) => {
  if (!geography) return "Unknown geography"
  switch (geography.kind) {
    case SearchGeographyKind.US:
      return "US"
    case SearchGeographyKind.NOT_APPLICABLE:
      return "Not applicable"
    case SearchGeographyKind.STATES:
      return geography.subdivisionCodes.join(", ")
    case SearchGeographyKind.COUNTIES:
      return geography.countyFipsCodes.map(formatCounty).join("; ")
    case SearchGeographyKind.FORMATION_JURISDICTION_UNKNOWN:
      return "Unknown formation state"
  }
}

// Where a Company Run sits in its Monitoring Run. Failure outranks every other
// phase, and deferral never records a finish time. Failure is the same two
// facts monitoring-operations.ts's isCompanyRunFailed counts in SQL.
export const companyRunPhase = (
  row: Pick<
    CompanyRunRow,
    "outcome" | "assessmentOutcome" | "admittedAt" | "finishedAt"
  >
) => {
  if (
    row.outcome === CompanyRunOutcome.EXCEPTION ||
    row.assessmentOutcome === AssessmentOutcome.FAILED
  )
    return "failed"
  if (row.outcome === CompanyRunOutcome.DEFERRED) return "deferred"
  if (row.finishedAt !== null) return "finished"
  return row.admittedAt === null ? "waiting" : "active"
}
export type CompanyRunPhase = ReturnType<typeof companyRunPhase>

export const statusToneByPhase: Record<CompanyRunPhase, string> = {
  failed: "text-assessment-critical",
  deferred: "text-assessment-stressed",
  active: "text-terminal-accent",
  waiting: "",
  finished: "",
}

const runStartFormat = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
})
export const formatRunStart = (iso: string) =>
  runStartFormat.format(new Date(iso))

export const pluralize = (count: number, noun: string) => {
  if (count === 1) return `${count} ${noun}`
  return `${count} ${noun}${noun.endsWith("h") ? "es" : "s"}`
}

// "3 Companies", or with a qualifier, "3 due Companies".
export const pluralCompanies = (count: number, qualifier?: string) =>
  `${count} ${qualifier ? `${qualifier} ` : ""}${count === 1 ? "Company" : "Companies"}`

export const companyActivity = (
  row: Pick<
    CompanyRunRow,
    | "outcome"
    | "assessmentId"
    | "assessmentOutcome"
    | "pendingResearchCount"
    | "pendingExtractionCount"
    | "pendingReviewCount"
    | "admittedAt"
  >
) => {
  if (row.outcome) return dataByCompanyRunOutcome[row.outcome].label
  if (row.assessmentId && row.assessmentOutcome === null)
    return "Assessment in progress"
  // Named as the stage strip names the stage, counted in what is pending.
  if (row.pendingResearchCount > 0)
    return `Research in progress (${pluralize(row.pendingResearchCount, "search")} running)`
  if (row.pendingExtractionCount > 0)
    return `Extraction in progress (${pluralize(row.pendingExtractionCount, "extraction")} pending)`
  if (row.pendingReviewCount > 0)
    return `Review labels in progress (${pluralize(row.pendingReviewCount, "model call")} pending)`
  return row.admittedAt === null
    ? "Awaiting admission"
    : "Admitted; no pending activity recorded"
}

export const elapsedTime = (start: string, end: string | null) => {
  const seconds = Math.max(
    0,
    Math.floor(
      ((end ? Date.parse(end) : Date.now()) - Date.parse(start)) / 1000
    )
  )
  const hours = Math.floor(seconds / 3600)
  const clock = `${Math.floor(seconds / 60) % 60}m ${seconds % 60}s`
  return hours > 0 ? `${hours}h ${clock}` : clock
}

export const doesCompanyNeedAttention = (row: CompanyRunRow) =>
  ["failed", "deferred"].includes(companyRunPhase(row)) ||
  row.isSufficiencyUnreadable ||
  row.categories?.some(
    (category) => category.outcome === ResearchSufficiencyOutcome.INSUFFICIENT
  ) === true
